import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/** One glint of sun on the water: where it sits, how it twinkles. */
interface Glint {
  x: number;
  y: number;
  len: number;
  rate: number;
  phase: number;
  rank: number;
}

/** One of the swallows darting over the lake (Dock Town's Salda birds). */
interface Swallow {
  x: number;
  y: number;
  base: number;
  dir: 1 | -1;
  speed: number;
  size: number;
  bob: number;
  bobRate: number;
  phase: number;
  flap: number;
  rank: number;
  shown: number;
}

/**
 * Salda's hero board (2026-10-16): Lake Salda on a clear day, light and airy like the theme. A pale sky
 * over rounded hills of pine on the far shore, soft and hazy with distance, mirrored faintly in the lake;
 * the water deep turquoise far off and paler toward us, then the clear shallows over the lake's white
 * shore, which runs across the bottom in a fixed S; the sun glinting on the water and a thin wash
 * lapping the shore; swallows darting over the water, as in Dock Town's Salda. A gust (the pointer, a
 * finger, the counter counting) sets more glints sparkling and brings more swallows out, faster (owner).
 * Laid out by the stage's height with fixed seeds, so the scene keeps one shape on every screen.
 */
@Component( {
  selector: 'app-hero-salda',
  template: '<canvas #canvas class="hs-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hs-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroSaldaComponent extends HeroMotifBase {

  private back: HTMLCanvasElement | null = null;
  private glints: Glint[] = [];
  private swallows: Swallow[] = [];
  private scaleK = 1;
  // the water's far edge and the shore's line, in page pixels
  private horizon = 0;
  private shoreAt: ( x: number ) => number = () => 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.2;
  }

  protected colorsChanged(): void {
    this.back = null;
  }

  protected build(): void {
    const W = this.width;
    const H = this.height;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, H / 760 ) );
    this.back = null;
    // low, so the hero's words sit over the sky and the hills, not the water
    this.horizon = H * 0.6;
    // the shore: a fixed S across the stage, a big swing with smaller ones on it (laid out by height)
    const UW = Math.min( W, H * 2.1 );
    this.shoreAt = ( x: number ) => {
      const t = ( x - W / 2 ) / UW * Math.PI * 2;
      return H * ( 0.84 + 0.026 * Math.sin( t * 0.9 + 0.6 ) + 0.01 * Math.sin( t * 2.6 + 1.9 ) + 0.0035 * Math.sin( t * 7 + 0.3 ) );
    };
    // the glints, from a fixed seed: thicker toward the sun's path, smaller and denser far off
    const rand = this.seededLocal( 11 );
    this.glints = [];
    const k = this.scaleK;
    for ( let i = 0; i < 260; i++ ) {
      const x = rand() * W;
      const depth = Math.pow( rand(), 1.3 );
      const y = this.horizon + H * 0.01 + ( this.shoreAt( x ) - this.horizon - H * 0.03 ) * depth;
      if ( y > this.shoreAt( x ) - H * 0.02 ) { continue; }
      this.glints.push( {
        x, y, len: ( 3 + 9 * depth + rand() * 6 ) * k,
        rate: 1 + Math.floor( rand() * 5 ), phase: rand() * Math.PI * 2, rank: rand(),
      } );
    }
    // the swallows: a pool, each out only while the gust is strong enough for its rank
    this.swallows = [];
    for ( let i = 0; i < 26; i++ ) {
      const far = rand();
      this.swallows.push( {
        x: rand() * W, y: 0, base: H * ( 0.05 + 0.23 * rand() ), dir: rand() < 0.5 ? 1 : -1,
        speed: ( 110 + rand() * 90 ) * k * ( 1 - 0.4 * far ), size: ( 16 - 7 * far ) * k,
        bob: ( 8 + rand() * 22 ) * k, bobRate: 0.6 + rand() * 0.9, phase: rand() * Math.PI * 2,
        flap: rand() * 10, rank: i === 0 ? 0 : rand(), shown: 0,
      } );
    }
  }

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const H = this.height;
    const scale = ctx.getTransform().a;
    if ( this.frozen ) {
      // the page's wallpaper: the swallows, still, all down the page (owner)
      this.drawWallSwallows( ctx );
      return;
    }
    if ( !this.back ) { this.bake( scale ); }
    if ( !this.back ) { return; }
    ctx.drawImage( this.back, 0, 0, W, H );
    this.drawWash( ctx );
    this.drawGlints( ctx );
    this.drawSwallows( ctx, dt );
  }

  /** The thin wash lapping the shore: a pale line that runs up and slips back, on the loop. */
  private drawWash( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const k = this.scaleK;
    const t = this.time * W0;
    const [ cream ] = this.five();
    const foam = this.mix( cream, [ 255, 255, 255 ], 0.6 );
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for ( const [ phase, a ] of [ [ 0, 0.3 ], [ Math.PI, 0.2 ] ] ) {
      // how far up the sand it has run (whole turns in the loop)
      const run = ( 0.5 + 0.5 * Math.sin( t * 4 + phase ) );
      ctx.strokeStyle = this.rgba( foam, a * ( 0.4 + 0.6 * run ) );
      ctx.lineWidth = 2.4 * k;
      ctx.beginPath();
      for ( let x = -4; x <= W + 4; x += 6 ) {
        const y = this.shoreAt( x ) - 2 * k - run * 5 * k + Math.sin( x * 0.03 + t * 2 + phase ) * 1.2 * k;
        if ( x === -4 ) { ctx.moveTo( x, y ); } else { ctx.lineTo( x, y ); }
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  /** The sun on the water: each glint a short bright streak flashing in its own rhythm; more in a gust. */
  private drawGlints( ctx: CanvasRenderingContext2D ): void {
    const t = this.time * W0;
    const e = this.energy;
    const share = 0.35 + 0.65 * e;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for ( const g of this.glints ) {
      if ( g.rank > share ) { continue; }
      const flash = Math.pow( Math.max( 0, Math.sin( t * g.rate + g.phase ) ), 8 );
      if ( flash < 0.03 ) { continue; }
      // a soft spark, wider than tall, never a hard dash
      ctx.save();
      ctx.translate( g.x, g.y );
      ctx.scale( g.len / 2, Math.max( 1, g.len * 0.14 ) );
      const sp = ctx.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      sp.addColorStop( 0, this.rgba( [ 255, 255, 250 ], flash * ( 0.6 + 0.3 * e ) ) );
      sp.addColorStop( 1, this.rgba( [ 255, 255, 250 ], 0 ) );
      ctx.fillStyle = sp;
      ctx.fillRect( -1, -1, 2, 2 );
      ctx.restore();
    }
    ctx.restore();
  }

  /**
   * The swallows: they dart across the sky over the lake, rising and dipping, beating in bursts and
   * gliding between; a couple at rest, many more and faster while the counter counts or under the
   * pointer (owner). Each comes out of the distance and flies off into it again.
   */
  private drawSwallows( ctx: CanvasRenderingContext2D, dt: number ): void {
    const W = this.width;
    const e = this.energy;
    const share = 0.08 + 0.92 * e;
    for ( const b of this.swallows ) {
      const want = b.rank <= share ? 1 : 0;
      // coming and going slowly, as if from and into the distance (owner: fading, but natural)
      b.shown = dt > 0 ? b.shown + ( want - b.shown ) * Math.min( 1, dt * 0.55 ) : want;
      if ( dt > 0 ) {
        b.x += b.dir * b.speed * ( 1 + 0.6 * e ) * dt;
        b.phase += dt * b.bobRate;
        b.flap += dt;
        const m = b.size * 3;
        if ( b.x > W + m ) { b.x = -m; } else if ( b.x < -m ) { b.x = W + m; }
      }
      if ( b.shown < 0.02 ) { continue; }
      // a swallow darts: a dip and a rise, and now and then a sharper swoop
      b.y = b.base + Math.sin( b.phase * 2 ) * b.bob + Math.pow( Math.max( 0, Math.sin( b.phase * 0.7 ) ), 6 ) * b.bob * 1.6;
      // beating in bursts, gliding in between (wings out), the wings sweeping smoothly through the
      // stroke rather than jumping from pose to pose (owner)
      const glide = Math.min( 1, Math.max( 0, ( Math.sin( b.flap * 1.3 ) - 0.2 ) / 0.4 ) );
      // two parts (owner): the arm leads the stroke, the hand follows a little behind it
      const theta = b.flap * Math.PI * 2 * 3.2;
      const arm = Math.sin( theta ) * ( 1 - glide );
      const hand = Math.sin( theta - 0.8 ) * ( 1 - glide );
      ctx.save();
      // a bird going away climbs a little and grows smaller as it fades, so it flies off rather than
      // vanishing where it is
      const away = 1 - b.shown;
      ctx.globalAlpha = Math.pow( b.shown, 0.7 ) * 0.92;
      ctx.translate( b.x, b.y - away * b.bob * 2.5 );
      ctx.scale( 1 - 0.55 * away, 1 - 0.55 * away );
      ctx.scale( b.dir, 1 );
      this.swallow( ctx, b.size, arm, hand );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * One swallow facing right (y down), shaped as Dock Town draws it: each wing in two parts, the arm
   * to the wrist at `arm` and the hand to the tip at `hand` (-1 raised, 0 out, 1 lowered, anything
   * between), so it opens and closes in two movements; swept back, paler beneath while raised; the forked tail with
   * its streamers, the body, the pale belly, the head with its warm throat. Coloured from the theme's
   * own teals and creams, never black (owner).
   */
  private swallow( ctx: CanvasRenderingContext2D, s: number, arm: number, hand: number ): void {
    const [ cream, , , pale, turquoise ] = this.five();
    // a tick lighter (owner)
    const bodyC = this.mix( this.mix( turquoise, [ 39, 120, 120 ], 0.5 ), pale, 0.12 );
    const blue = this.rgba( bodyC, 1 );
    const pose = ( a: number[], t: number ) => t <= 0 ? a[ 1 ] + ( a[ 0 ] - a[ 1 ] ) * -t : a[ 1 ] + ( a[ 2 ] - a[ 1 ] ) * t;
    const bendY = pose( [ -0.36, -0.2, 0.06 ], arm );
    const tipY = pose( [ -0.8, -0.1, 0.46 ], hand ), tipX = pose( [ 0.84, 1.06, 0.9 ], hand );
    const wing = this.rgba( this.mix( bodyC, this.mix( pale, bodyC, 0.35 ), Math.max( 0, -hand ) * 0.8 ), 1 );
    const poly = ( pts: number[][], fill: string ) => {
      ctx.fillStyle = fill;
      ctx.beginPath();
      pts.forEach( ( [ x, y ], i ) => i ? ctx.lineTo( x * s, y * s ) : ctx.moveTo( x * s, y * s ) );
      ctx.closePath();
      ctx.fill();
    };
    for ( const side of [ -1, 1 ] ) {
      poly( [ [ side * 0.06, -0.05 ], [ side * 0.4 - 0.06, bendY ], [ side * tipX - 0.24, tipY ],
        [ side * 0.32 - 0.16, bendY + 0.13 ], [ side * 0.03 - 0.08, 0.08 ] ], wing );
    }
    poly( [ [ -0.2, -0.02 ], [ -0.66, -0.16 ], [ -0.44, 0.02 ], [ -0.66, 0.18 ], [ -0.2, 0.07 ] ], blue );
    ctx.strokeStyle = blue;
    ctx.lineWidth = 0.05 * s;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo( -0.6 * s, -0.14 * s );
    ctx.lineTo( -0.9 * s, -0.24 * s );
    ctx.moveTo( -0.6 * s, 0.16 * s );
    ctx.lineTo( -0.9 * s, 0.27 * s );
    ctx.stroke();
    const oval = ( x: number, y: number, rx: number, ry: number, fill: string ) => {
      ctx.fillStyle = fill;
      ctx.beginPath();
      ctx.ellipse( x * s, y * s, rx * s, ry * s, 0, 0, Math.PI * 2 );
      ctx.fill();
    };
    oval( 0.02, 0.02, 0.28, 0.11, blue );
    oval( 0, 0.07, 0.22, 0.06, this.rgba( cream, 1 ) );
    oval( 0.27, -0.02, 0.1, 0.1, blue );
    oval( 0.3, 0.05, 0.065, 0.065, 'rgb(143,96,85)' );
    poly( [ [ 0.35, -0.04 ], [ 0.44, -0.01 ], [ 0.35, 0.01 ] ], this.rgba( this.mix( bodyC, [ 20, 30, 35 ], 0.4 ), 1 ) );
  }

  /** The wallpaper: Salda's swallows scattered still down the page, each caught at its own point in the beat (owner). */
  private drawWallSwallows( ctx: CanvasRenderingContext2D ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const rand = this.seededLocal( 5 );
    const n = Math.max( 4, Math.round( W * H / 70000 ) );
    for ( let i = 0; i < n; i++ ) {
      const theta = rand() * Math.PI * 2;
      ctx.save();
      ctx.globalAlpha = 0.45 + rand() * 0.35;
      ctx.translate( rand() * W, rand() * H );
      ctx.scale( rand() < 0.5 ? 1 : -1, 1 );
      this.swallow( ctx, ( 9 + rand() * 7 ) * k, Math.sin( theta ), Math.sin( theta - 0.8 ) );
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  }

  /**
   * The still picture, painted once: the sky, the far hills of pine in two hazy layers, the lake with
   * the hills' faint reflection and soft bands of light on it, the shallows, and the white shore.
   */
  private bake( scale: number ): void {
    const W = this.width;
    const H = this.height;
    const k = this.scaleK;
    const UW = Math.min( W, H * 2.1 );
    const [ cream, beige, taupe, pale, turquoise ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const pine: RGB = [ 39, 120, 120 ];
    const deep = this.mix( turquoise, [ 15, 90, 110 ], 0.35 );
    const horizon = this.horizon;
    const c = document.createElement( 'canvas' );
    c.width = Math.ceil( W * scale );
    c.height = Math.ceil( H * scale );
    const g = c.getContext( '2d' )!;
    g.scale( scale, scale );

    // the sky: a clear pale blue above, whitening toward the hills
    const sky = g.createLinearGradient( 0, 0, 0, horizon );
    sky.addColorStop( 0, this.rgba( this.mix( pale, turquoise, 0.25 ), 1 ) );
    sky.addColorStop( 0.6, this.rgba( this.mix( pale, white, 0.45 ), 1 ) );
    sky.addColorStop( 1, this.rgba( this.mix( cream, white, 0.4 ), 1 ) );
    g.fillStyle = sky;
    g.fillRect( 0, 0, W, horizon + 2 );
    // a few soft high clouds
    const cr = this.seededLocal( 3 );
    for ( let i = 0; i < 8; i++ ) {
      g.save();
      g.translate( W / 2 + ( cr() - 0.5 ) * 1.2 * Math.max( W, UW ), H * ( 0.06 + cr() * 0.24 ) );
      g.scale( ( 90 + cr() * 160 ) * k, ( 10 + cr() * 14 ) * k );
      const wg = g.createRadialGradient( 0, -0.2, 0, 0, 0, 1 );
      wg.addColorStop( 0, this.rgba( white, 0.55 ) );
      wg.addColorStop( 1, this.rgba( white, 0 ) );
      g.fillStyle = wg;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }

    // the far hills: rounded, forested, in two layers, the farther paler with haze; their tops
    // carry a fine roughness of pines. Laid out by the height, repeating to the edges.
    const ridge = ( base: number, amp: number, seed: number ) => {
      const rr = this.seededLocal( seed );
      const waves = [ 0, 1, 2, 3 ].map( i => ( { f: 2.2 * Math.pow( 2.3, i ), p: rr() * 6.28, a: Math.pow( 0.45, i ) } ) );
      const fine: number[] = [];
      for ( let i = 0; i < 4000; i++ ) { fine.push( rr() - 0.5 ); }
      return ( x: number ) => {
        const t = ( x - W / 2 ) / UW;
        let h = 0;
        for ( const w of waves ) { h += w.a * ( 0.5 + 0.5 * Math.sin( t * w.f + w.p ) ); }
        const fi = fine[ Math.abs( Math.round( t * 900 ) ) % fine.length ];
        return base - amp * h / 1.8 - fi * H * 0.003;
      };
    };
    const farRidge = ridge( horizon - H * 0.025, H * 0.12, 21 );
    const nearRidge = ridge( horizon, H * 0.08, 22 );
    const paintRidge = ( r: ( x: number ) => number, bottom: number, top: RGB, low: RGB ) => {
      const hg = g.createLinearGradient( 0, bottom - H * 0.16, 0, bottom );
      hg.addColorStop( 0, this.rgba( top, 1 ) );
      hg.addColorStop( 1, this.rgba( low, 1 ) );
      g.fillStyle = hg;
      g.beginPath();
      g.moveTo( -2, bottom );
      for ( let x = -2; x <= W + 2; x += 2 ) { g.lineTo( x, r( x ) ); }
      g.lineTo( W + 2, bottom );
      g.closePath();
      g.fill();
    };
    const haze = this.mix( pale, white, 0.4 );
    paintRidge( farRidge, horizon + 1, this.mix( this.mix( pine, pale, 0.55 ), haze, 0.35 ), this.mix( this.mix( pine, pale, 0.6 ), haze, 0.5 ) );
    const forest: RGB = this.mix( pine, [ 15, 147, 99 ], 0.35 );
    paintRidge( nearRidge, horizon + 1, this.mix( forest, pale, 0.25 ), this.mix( this.mix( forest, pale, 0.4 ), haze, 0.2 ) );
    // a light haze lying along the far shore
    const hz = g.createLinearGradient( 0, horizon - H * 0.05, 0, horizon + 1 );
    hz.addColorStop( 0, this.rgba( haze, 0 ) );
    hz.addColorStop( 1, this.rgba( haze, 0.5 ) );
    g.fillStyle = hz;
    g.fillRect( 0, horizon - H * 0.05, W, H * 0.05 + 1 );

    // the lake: deep turquoise far off, paler toward us
    const lake = g.createLinearGradient( 0, horizon, 0, H * 0.87 );
    lake.addColorStop( 0, this.rgba( this.mix( turquoise, pale, 0.2 ), 1 ) );
    lake.addColorStop( 0.3, this.rgba( this.mix( deep, turquoise, 0.4 ), 1 ) );
    lake.addColorStop( 1, this.rgba( this.mix( turquoise, pale, 0.55 ), 1 ) );
    g.fillStyle = lake;
    g.fillRect( 0, horizon, W, H - horizon );
    // the far edge of the water holds the sky's light: a pale strip melting into the turquoise
    const edge = g.createLinearGradient( 0, horizon, 0, horizon + H * 0.04 );
    edge.addColorStop( 0, this.rgba( this.mix( pale, white, 0.5 ), 0.75 ) );
    edge.addColorStop( 1, this.rgba( pale, 0 ) );
    g.fillStyle = edge;
    g.fillRect( 0, horizon, W, H * 0.04 );
    // the hills mirrored in it, soft (painted small and drawn up), fading as it comes toward us
    const rs = 0.25;
    const rc = document.createElement( 'canvas' );
    rc.width = Math.ceil( W * scale * rs );
    rc.height = Math.ceil( H * scale * rs );
    const r2 = rc.getContext( '2d' )!;
    r2.scale( scale * rs, scale * rs );
    r2.fillStyle = this.rgba( this.mix( pine, deep, 0.4 ), 1 );
    r2.beginPath();
    r2.moveTo( -2, horizon );
    for ( let x = -2; x <= W + 2; x += 4 ) { r2.lineTo( x, horizon + ( horizon - nearRidge( x ) ) * 0.8 ); }
    r2.lineTo( W + 2, horizon );
    r2.closePath();
    r2.fill();
    r2.globalCompositeOperation = 'destination-in';
    const rf = r2.createLinearGradient( 0, horizon, 0, horizon + H * 0.1 );
    rf.addColorStop( 0, 'rgba(0,0,0,0.35)' );
    rf.addColorStop( 1, 'rgba(0,0,0,0)' );
    r2.fillStyle = rf;
    r2.fillRect( 0, 0, W, H );
    g.drawImage( rc, 0, 0, Math.floor( W * scale * rs ), Math.floor( H * scale * rs ), 0, 0, W, H );
    // soft bands of light lying across the water, wider toward us
    const br = this.seededLocal( 7 );
    for ( let i = 0; i < 24; i++ ) {
      const y = horizon + ( H * 0.83 - horizon ) * Math.pow( br(), 0.8 );
      const d = ( y - horizon ) / ( H * 0.3 );
      g.save();
      g.translate( W / 2 + ( br() - 0.5 ) * 1.4 * Math.max( W, UW ), y );
      g.scale( ( 80 + br() * 200 ) * k * ( 0.5 + d ), ( 1 + 2.5 * d ) * k );
      const lg = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      lg.addColorStop( 0, this.rgba( this.mix( pale, white, 0.4 ), 0.28 ) );
      lg.addColorStop( 1, this.rgba( pale, 0 ) );
      g.fillStyle = lg;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }

    // the shallows: the white bottom showing through the clear water, the turquoise thinning to the shore
    const shoreTop = H * 0.76;
    g.save();
    g.beginPath();
    g.moveTo( -2, H );
    for ( let x = -2; x <= W + 2; x += 3 ) { g.lineTo( x, this.shoreAt( x ) - H * 0.06 ); }
    g.lineTo( W + 2, H );
    g.closePath();
    g.clip();
    const sh = g.createLinearGradient( 0, shoreTop, 0, H * 0.87 );
    sh.addColorStop( 0, this.rgba( this.mix( turquoise, pale, 0.5 ), 0 ) );
    sh.addColorStop( 0.5, this.rgba( this.mix( pale, cream, 0.3 ), 0.55 ) );
    sh.addColorStop( 1, this.rgba( this.mix( pale, white, 0.5 ), 0.85 ) );
    g.fillStyle = sh;
    g.fillRect( 0, shoreTop, W, H - shoreTop );
    g.restore();

    // the white shore, the lake's hydromagnesite: cream at the water, warmer beige farther up, with soft
    // drifts of taupe across it
    const sand = g.createLinearGradient( 0, H * 0.8, 0, H );
    sand.addColorStop( 0, this.rgba( this.mix( cream, white, 0.6 ), 1 ) );
    sand.addColorStop( 0.5, this.rgba( cream, 1 ) );
    sand.addColorStop( 1, this.rgba( this.mix( cream, beige, 0.6 ), 1 ) );
    g.fillStyle = sand;
    g.beginPath();
    g.moveTo( -2, H );
    for ( let x = -2; x <= W + 2; x += 3 ) { g.lineTo( x, this.shoreAt( x ) ); }
    g.lineTo( W + 2, H );
    g.closePath();
    g.fill();
    g.save();
    g.clip();
    const sr = this.seededLocal( 9 );
    for ( let i = 0; i < 40; i++ ) {
      const x = W / 2 + ( sr() - 0.5 ) * 1.3 * Math.max( W, UW );
      const y = this.shoreAt( x ) + ( H - this.shoreAt( x ) ) * ( 0.15 + sr() * 0.85 );
      g.save();
      g.translate( x, y );
      g.rotate( ( sr() - 0.5 ) * 0.1 );
      g.scale( ( 60 + sr() * 140 ) * k, ( 3 + sr() * 5 ) * k );
      const dg = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      dg.addColorStop( 0, this.rgba( this.mix( beige, taupe, 0.3 ), 0.25 ) );
      dg.addColorStop( 1, this.rgba( beige, 0 ) );
      g.fillStyle = dg;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    g.restore();
    // where the water meets the sand: a soft wet line
    g.strokeStyle = this.rgba( this.mix( beige, turquoise, 0.25 ), 0.35 );
    g.lineWidth = 3 * k;
    g.beginPath();
    for ( let x = -2; x <= W + 2; x += 3 ) {
      const y = this.shoreAt( x ) + 1.5 * k;
      if ( x === -2 ) { g.moveTo( x, y ); } else { g.lineTo( x, y ); }
    }
    g.stroke();

    // a clearing of light behind the hero's words, so they read over the hills and water
    for ( const b of this.blocks ) {
      g.save();
      g.translate( b.x + b.w / 2, b.y + b.h / 2 );
      g.scale( b.w / 2 + 70 * k, b.h / 2 + 40 * k );
      const cl = g.createRadialGradient( 0, 0, 0, 0, 0, 1 );
      cl.addColorStop( 0, this.rgba( this.mix( cream, white, 0.5 ), 0.4 ) );
      cl.addColorStop( 1, this.rgba( white, 0 ) );
      g.fillStyle = cl;
      g.fillRect( -1, -1, 2, 2 );
      g.restore();
    }
    this.back = c;
  }

  private seededLocal( seed: number ): () => number {
    let s = ( seed * 2654435761 ) >>> 0;
    return () => {
      s = ( s + 0x6D2B79F5 ) >>> 0;
      let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
      t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
      return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
  }

  /** The colour circle's five, or Salda's own: cream, beige, taupe, pale turquoise, turquoise. */
  private five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    return [ [ 253, 246, 231 ], [ 236, 225, 201 ], [ 165, 147, 121 ], [ 184, 228, 236 ], [ 19, 151, 174 ] ];
  }
}
